import type { EpisodeResponse, SubJourneyResponse } from '@manasil/shared';
import { STORY_BANNER } from '@manasil/shared';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Shell } from '../components/Shell.js';
import { StoryMediaFields, useStoryMedia } from '../components/StoryMediaFields.js';
import {
  Empty,
  Field,
  Loading,
  Modal,
  errorMessage,
  formatDuration,
  useToast,
} from '../components/ui.js';
import { resolveMediaUrl } from '../lib/api.js';
import {
  BANNER_SPEC,
  IMAGE_ACCEPT,
  type ImageCheck,
  inspectImage,
  removeSubJourneyBanner,
  uploadSubJourneyBanner,
} from '../lib/image.js';
import { usePlayer } from '../lib/player.js';
import {
  useCreateEpisode,
  useDeleteEpisode,
  useEpisodes,
  useInvalidateEpisodes,
  useJourney,
  useSubJourney,
  useUpdateEpisode,
} from '../lib/queries.js';

/**
 * One sub-journey — an adhikaram — and its episodes, the kurals.
 *
 * This is where most of a collection's authoring time goes: ten kurals, each with two
 * lines, a meaning, a recording and (optionally) art. Episodes are tiles like days are,
 * because the banner and the audio state are the two things worth seeing at a glance.
 */
export function SubJourneyPage() {
  const { id = '', subJourneyId = '' } = useParams();
  const { data: journey } = useJourney(id);
  const { data: subJourney, isLoading } = useSubJourney(subJourneyId);
  const { data: episodes } = useEpisodes(subJourneyId);
  const [adding, setAdding] = useState(false);

  if (isLoading || !subJourney || !journey) {
    return (
      <Shell title="Sub-journey">
        <Loading />
      </Shell>
    );
  }

  const count = episodes?.length ?? 0;
  const ready = episodes?.filter((episode) => episode.story.hasAudio).length ?? 0;

  return (
    <Shell
      title={`${subJourney.number} · ${subJourney.title}`}
      subtitle={`${journey.title} · ${count} episodes · ${ready} with audio${
        subJourney.durationSec > 0 ? ` · ${formatDuration(subJourney.durationSec)}` : ''
      }`}
      actions={
        <div className="row" style={{ gap: 9 }}>
          <Link to={`/journeys/${id}`} className="btn btn-ghost btn-sm">
            Back to {journey.title}
          </Link>
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
            Add episode
          </button>
        </div>
      }
    >
      <SubJourneyBannerCard
        journeyId={id}
        subJourney={subJourney}
        episodesWithOwnArt={episodes?.filter((episode) => episode.story.hasBanner).length ?? 0}
      />

      <div className="card">
        <div className="card-head">
          <div>
            <h3>Episodes</h3>
            <p className="hint">
              {subJourney.description ||
                'Numbered through the whole collection, not per sub-journey.'}
            </p>
          </div>
        </div>

        {count === 0 ? (
          <Empty
            mark="📜"
            title="No episodes yet"
            hint="Adding an episode creates its story slot for the audio at the same time."
          />
        ) : (
          <div className="day-grid">
            {episodes?.map((episode) => (
              <EpisodeTile
                key={episode.id}
                journeyId={id}
                subJourneyId={subJourneyId}
                episode={episode}
                inheritedBanner={subJourney.bannerUrl}
              />
            ))}
          </div>
        )}
      </div>

      {adding ? (
        <AddEpisodeModal
          journeyId={id}
          subJourneyId={subJourneyId}
          nextNumber={Math.max(0, ...(episodes?.map((episode) => episode.number) ?? [])) + 1}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </Shell>
  );
}

/* ─────────────────────── one episode ─────────────────────── */

function EpisodeTile({
  journeyId,
  subJourneyId,
  episode,
  inheritedBanner,
}: {
  journeyId: string;
  subJourneyId: string;
  episode: EpisodeResponse;
  /** The adhikaram's banner, which the player shows when the episode has none of its own. */
  inheritedBanner: string | null;
}) {
  const remove = useDeleteEpisode(journeyId, subJourneyId);
  const player = usePlayer();
  const { push } = useToast();
  const [editing, setEditing] = useState(false);

  const story = episode.story;
  const isCurrent = player.now?.storyId === story.id;

  function listen() {
    if (!story.hasAudio) return;
    void player.play({
      storyId: story.id,
      title: episode.line1,
      context: `Kural ${episode.number}`,
    });
  }

  function confirmRemove() {
    if (!confirm(`Delete episode ${episode.number}? Its audio goes with it.`)) return;
    remove.mutate(episode.id, {
      onSuccess: () => push(`Removed episode ${episode.number}`),
      onError: (caught) => push(errorMessage(caught), 'err'),
    });
  }

  return (
    <>
      <article className="day-tile">
        <div className="day-art">
          {story.bannerUrl ? (
            <img src={resolveMediaUrl(story.bannerUrl)} alt="" />
          ) : inheritedBanner ? (
            <img
              src={resolveMediaUrl(inheritedBanner)}
              alt=""
              title="Inherited from the adhikaram"
              style={{ opacity: 0.8 }}
            />
          ) : (
            <span className="day-art-none" title="No banner for this episode">
              ▧
            </span>
          )}
          <span className="day-badge nums">#{episode.number}</span>
          {story.hasAudio ? (
            <button
              type="button"
              className="day-play"
              onClick={listen}
              aria-pressed={isCurrent && player.playing}
              aria-label={`Play episode ${episode.number}`}
              title="Listen"
            >
              {isCurrent && player.playing ? '❚❚' : '▶'}
            </button>
          ) : null}
        </div>

        <div className="day-body">
          <div className="day-title kural-lines">
            <span>{episode.line1}</span>
            {episode.line2 ? <span>{episode.line2}</span> : null}
          </div>
          <div className="day-meta">
            {story.hasAudio ? (
              <span className="pill pill-ok">
                <span className="dot" />
                {formatDuration(story.durationSec)}
              </span>
            ) : (
              <span className="pill pill-missing">No audio</span>
            )}
            {!story.bannerUrl && !inheritedBanner ? (
              <span className="pill pill-missing">No banner</span>
            ) : null}
            {!episode.meaning ? <span className="pill pill-missing">No meaning</span> : null}
          </div>
          {episode.meaning ? (
            <div className="day-story dim kural-meaning">{episode.meaning}</div>
          ) : null}
        </div>

        <div className="day-actions">
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setEditing(true)}
            aria-label={`Edit episode ${episode.number}`}
            title="Edit"
          >
            ✎
          </button>
          <button
            type="button"
            className="btn btn-icon btn-danger"
            onClick={confirmRemove}
            disabled={remove.isPending}
            aria-label={`Delete episode ${episode.number}`}
            title="Delete"
          >
            ✕
          </button>
        </div>
      </article>

      {editing ? (
        <EditEpisodeModal
          journeyId={journeyId}
          subJourneyId={subJourneyId}
          episode={episode}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </>
  );
}

/* ─────────────────────── the adhikaram banner ─────────────────────── */

/**
 * One square image for the whole adhikaram. Every kural under it shows this in the player
 * unless it has art of its own — so ten kurals need one upload, not ten. Immediate, not
 * deferred to a Save: the card is the whole form.
 */
function SubJourneyBannerCard({
  journeyId,
  subJourney,
  episodesWithOwnArt,
}: {
  journeyId: string;
  subJourney: SubJourneyResponse;
  episodesWithOwnArt: number;
}) {
  const invalidate = useInvalidateEpisodes(journeyId, subJourney.id);
  const { push } = useToast();
  const [pending, setPending] = useState<{ file: File; check: ImageCheck } | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (pending) URL.revokeObjectURL(pending.check.previewUrl);
    };
  }, [pending]);

  async function choose(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    try {
      setPending({ file, check: await inspectImage(file, BANNER_SPEC) });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  async function upload() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      setProgress(0);
      await uploadSubJourneyBanner(subJourney.id, pending.file, pending.check, setProgress);
      setPending(null);
      invalidate();
      push('Adhikaram banner saved');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  async function remove() {
    if (!confirm('Remove this banner? Kurals without art of their own fall back to the cover.'))
      return;
    setBusy(true);
    try {
      await removeSubJourneyBanner(subJourney.id);
      invalidate();
      push('Banner removed');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  const shown = pending
    ? pending.check.previewUrl
    : subJourney.bannerUrl
      ? resolveMediaUrl(subJourney.bannerUrl)
      : null;
  const blocked = pending !== null && pending.check.problems.length > 0;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Adhikaram banner</h3>
          <p className="hint">
            Shown in the player for every kural here that has no banner of its own
            {episodesWithOwnArt > 0 ? ` (${episodesWithOwnArt} have their own)` : ''}. Without it
            they fall back to the journey cover.
          </p>
        </div>
      </div>
      <div className="card-pad stack" style={{ gap: 12 }}>
        {error ? <div className="banner">{error}</div> : null}
        <div className="audio-box">
          <div className="banner-thumb" style={{ width: 96, height: 96 }}>
            {shown ? (
              <>
                <img src={shown} alt="" />
                <div className="banner-scrim" />
              </>
            ) : (
              <span className="banner-empty">No art</span>
            )}
          </div>
          <div className="grow">
            {pending ? (
              <>
                <div className="audio-name truncate">{pending.file.name}</div>
                <div className="hint nums">
                  {pending.check.width} × {pending.check.height} ·{' '}
                  {Math.round(pending.file.size / 1024)} KB
                </div>
              </>
            ) : subJourney.hasBanner ? (
              <div className="hint nums">
                {subJourney.bannerWidth} × {subJourney.bannerHeight} · uploaded
              </div>
            ) : (
              <div className="hint">
                {STORY_BANNER.width} × {STORY_BANNER.height} square · JPEG · keep the subject in the
                top half, the player darkens the bottom.
              </div>
            )}
          </div>
          {pending ? (
            <>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setPending(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={upload}
                disabled={busy || blocked}
              >
                {progress !== null ? `Uploading ${Math.round(progress * 100)}%` : 'Upload'}
              </button>
            </>
          ) : (
            <>
              {subJourney.hasBanner ? (
                <button type="button" className="btn btn-sm" onClick={remove} disabled={busy}>
                  Remove
                </button>
              ) : null}
              <label className={`btn btn-sm file-btn${subJourney.hasBanner ? '' : ' btn-primary'}`}>
                {subJourney.hasBanner ? 'Replace' : 'Choose image'}
                <input type="file" accept={IMAGE_ACCEPT} hidden onChange={choose} />
              </label>
            </>
          )}
        </div>
        {pending?.check.problems.map((problem) => (
          <div key={problem} className="banner">
            {problem}
          </div>
        ))}
        {pending?.check.warnings.map((warning) => (
          <span key={warning} className="hint">
            {warning}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────── the kural text, shared by both forms ─────────────────────── */

function KuralFields({
  number,
  setNumber,
  line1,
  setLine1,
  line2,
  setLine2,
  meaning,
  setMeaning,
  numberHint,
}: {
  number: string;
  setNumber: (value: string) => void;
  line1: string;
  setLine1: (value: string) => void;
  line2: string;
  setLine2: (value: string) => void;
  meaning: string;
  setMeaning: (value: string) => void;
  numberHint: string;
}) {
  return (
    <>
      <Field label="Number" hint={numberHint}>
        <input
          className="input nums"
          inputMode="numeric"
          value={number}
          onChange={(event) => setNumber(event.target.value)}
          placeholder="391"
          style={{ maxWidth: 140 }}
        />
      </Field>

      <Field label="First line">
        <input
          className="input"
          value={line1}
          onChange={(event) => setLine1(event.target.value)}
          placeholder="கற்க கசடறக் கற்பவை கற்றபின்"
          lang="ta"
        />
      </Field>

      <Field label="Second line">
        <input
          className="input"
          value={line2}
          onChange={(event) => setLine2(event.target.value)}
          placeholder="நிற்க அதற்குத் தக."
          lang="ta"
        />
      </Field>

      <Field label="Meaning (பொருள்)" hint="Shown under the player and on the detail screen.">
        <textarea
          className="textarea"
          value={meaning}
          onChange={(event) => setMeaning(event.target.value)}
          placeholder="கற்கத் தகுந்த நூல்களைக் குற்றமறக் கற்க வேண்டும்…"
          lang="ta"
        />
      </Field>
    </>
  );
}

function parseNumber(value: string): number | undefined | null {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
}

/* ─────────────────────── add ─────────────────────── */

function AddEpisodeModal({
  journeyId,
  subJourneyId,
  nextNumber,
  onClose,
}: {
  journeyId: string;
  subJourneyId: string;
  nextNumber: number;
  onClose: () => void;
}) {
  const create = useCreateEpisode(journeyId, subJourneyId);
  const { push } = useToast();
  const [number, setNumber] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [meaning, setMeaning] = useState('');
  const [error, setError] = useState<string | null>(null);

  const parsed = parseNumber(number);

  async function submit() {
    setError(null);
    try {
      const episode = await create.mutateAsync({
        ...(parsed ? { number: parsed } : {}),
        line1: line1.trim(),
        line2: line2.trim(),
        meaning: meaning.trim(),
      });
      push(`Episode ${episode.number} added`);
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <Modal
      title="Add episode"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={create.isPending || line1.trim().length === 0 || parsed === null}
          >
            {create.isPending ? 'Adding…' : 'Add episode'}
          </button>
        </>
      }
    >
      {error ? <div className="banner">{error}</div> : null}
      <KuralFields
        number={number}
        setNumber={setNumber}
        line1={line1}
        setLine1={setLine1}
        line2={line2}
        setLine2={setLine2}
        meaning={meaning}
        setMeaning={setMeaning}
        numberHint={`Blank takes the next in the collection (${nextNumber} here). Audio is added after.`}
      />
    </Modal>
  );
}

/* ─────────────────────── edit: text, audio and banner in one form ─────────────────────── */

function EditEpisodeModal({
  journeyId,
  subJourneyId,
  episode,
  onClose,
}: {
  journeyId: string;
  subJourneyId: string;
  episode: EpisodeResponse;
  onClose: () => void;
}) {
  const update = useUpdateEpisode(journeyId, subJourneyId);
  const invalidate = useInvalidateEpisodes(journeyId, subJourneyId);
  const { push } = useToast();

  const [number, setNumber] = useState(String(episode.number));
  const [line1, setLine1] = useState(episode.line1);
  const [line2, setLine2] = useState(episode.line2);
  const [meaning, setMeaning] = useState(episode.meaning);
  const [progress, setProgress] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const media = useStoryMedia(episode.story, setError);
  const parsed = parseNumber(number);

  const textDirty =
    (parsed !== undefined && parsed !== episode.number) ||
    line1.trim() !== episode.line1 ||
    line2.trim() !== episode.line2 ||
    meaning.trim() !== episode.meaning;

  const canSave =
    line1.trim().length > 0 &&
    parsed !== null &&
    (textDirty || media.dirty) &&
    !saving &&
    !media.blocked;

  async function save() {
    setError(null);
    setSaving(true);
    try {
      if (await media.apply(episode.story.id, setProgress)) invalidate();
      if (textDirty) {
        await update.mutateAsync({
          episodeId: episode.id,
          ...(parsed && parsed !== episode.number ? { number: parsed } : {}),
          line1: line1.trim(),
          line2: line2.trim(),
          meaning: meaning.trim(),
        });
      }
      push(`Episode ${parsed ?? episode.number} saved`);
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      title={`Episode ${episode.number}`}
      onClose={saving ? () => undefined : onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={!canSave}>
            {progress !== null
              ? `Uploading ${Math.round(progress * 100)}%`
              : saving
                ? 'Saving…'
                : 'Save changes'}
          </button>
        </>
      }
    >
      {error ? <div className="banner">{error}</div> : null}
      <KuralFields
        number={number}
        setNumber={setNumber}
        line1={line1}
        setLine1={setLine1}
        line2={line2}
        setLine2={setLine2}
        meaning={meaning}
        setMeaning={setMeaning}
        numberHint="Unique across the whole collection."
      />
      <StoryMediaFields
        media={media}
        saving={saving}
        progress={progress}
        playContext={`Kural ${episode.number}`}
        emptyHint="The collection cannot go live without it."
      />
    </Modal>
  );
}
